import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Clock, ArrowRight, X } from 'lucide-react';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { navigateToUpgrade } from '../features/subscription/upgradeNav';
import './TrialBanner.css';

const TrialBanner = () => {
  const navigate = useNavigate();
  const { getAxios } = useSecureAuth();
  const [trialInfo, setTrialInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchTrialStatus = useCallback(async () => {
    try {
      const axios = getAxios();
      const response = await axios.get('/merchant/billing/subscription');

      if (response.data.success && response.data.subscription) {
        const sub = response.data.subscription;
        const activeSub = sub.activeSubscription;

        // Check if user is on trial
        if (activeSub?.isOnTrial && activeSub?.trialEndsAt) {
          const endsAt = new Date(activeSub.trialEndsAt);
          const now = new Date();
          const daysRemaining = Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24));

          if (daysRemaining > 0) {
            setTrialInfo({
              tier: activeSub.tier,
              daysRemaining,
              endsAt: endsAt.toLocaleDateString(),
              isUrgent: daysRemaining <= 3
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
    } finally {
      setLoading(false);
    }
  }, [getAxios]);

  useEffect(() => {
    fetchTrialStatus();
  }, [fetchTrialStatus]);

  if (loading || !trialInfo || dismissed) {
    return null;
  }

  return (
    <div className={`trial-banner ${trialInfo.isUrgent ? 'trial-banner-urgent' : ''}`}>
      <div className="trial-banner-content">
        <div className="trial-banner-info">
          {trialInfo.isUrgent ? (
            <Clock size={18} className="trial-banner-icon" />
          ) : (
            <Gift size={18} className="trial-banner-icon" />
          )}
          <span>
            {trialInfo.isUrgent ? (
              <>
                <strong>Only {trialInfo.daysRemaining} day{trialInfo.daysRemaining !== 1 ? 's' : ''} left</strong> on your {trialInfo.tier} trial!
              </>
            ) : (
              <>
                You're on a <strong>{trialInfo.tier}</strong> trial &bull; {trialInfo.daysRemaining} day{trialInfo.daysRemaining !== 1 ? 's' : ''} remaining
              </>
            )}
          </span>
        </div>
        <div className="trial-banner-actions">
          <button
            className="trial-banner-upgrade"
            onClick={() => navigateToUpgrade(navigate)}
          >
            Upgrade Now <ArrowRight size={14} />
          </button>
          <button
            className="trial-banner-dismiss"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
